import { Head, Link } from '@inertiajs/react'
import Layout from '../../components/Layout'
import MeetingFilterBar from '../../components/meetings/MeetingFilterBar'
import MeetingPagination from '../../components/meetings/MeetingPagination'

interface MeetingRow {
  id: number
  status: string
  agenda_name: string
  facilitator_name: string | null
  scheduled_date: string | null
  started_at: string | null
  ended_at: string | null
  duration_minutes: number | null
  rating: number | null
  sections_count: number
  action_items_count: number
}

interface TeamData {
  id: number
  name: string
  description: string | null
  member_count: number
}

interface Filters {
  status: string
}

interface Props {
  team: TeamData
  meetings: MeetingRow[]
  filters: Filters
  page: number
  total_count: number
  per_page: number
  active_tab: string
}

function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m} min`
}

function statusLabel(status: string) {
  switch (status) {
    case 'in_progress': return 'In Progress'
    case 'completed': return 'Completed'
    case 'cancelled': return 'Cancelled'
    default: return status
  }
}

export default function Meetings({ team, meetings, filters, page, total_count, per_page, active_tab }: Props) {
  const inProgressMeetings = meetings.filter(m => m.status === 'in_progress')
  const pastMeetings = meetings.filter(m => m.status !== 'in_progress')

  const basePath = `/teams/${team.id}/meetings`
  const thClass = "px-5 py-3 text-left text-[12px] font-medium text-text-muted uppercase tracking-wider"

  return (
    <Layout team={team} activeTab={active_tab}>
      <Head title={`Meeting History \u2014 ${team.name}`} />
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-text-primary">Meeting History</h1>
        </div>

        <MeetingFilterBar
          basePath={basePath}
          filters={filters}
          totalCount={total_count}
          showScheduled={false}
        />

        {inProgressMeetings.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-semibold text-text-primary mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-pinnacle-red animate-pulse" />
              In Progress
            </h2>
            <div className="space-y-3">
              {inProgressMeetings.map(meeting => (
                <Link
                  key={meeting.id}
                  href={`/meetings/${meeting.id}`}
                  className="block bg-white rounded-lg border border-pinnacle-red/20 p-4 hover:border-pinnacle-red/40 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-text-primary">{meeting.agenda_name}</h3>
                        <span className="text-[12px] font-bold uppercase px-2 py-0.5 rounded-full bg-pinnacle-red/10 text-pinnacle-red">Live</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-text-secondary mt-1">
                        {meeting.facilitator_name && <span>Led by {meeting.facilitator_name}</span>}
                        {meeting.started_at && (
                          <>
                            <span className="text-text-muted">{'\u00B7'}</span>
                            <span>Started {meeting.started_at}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-pinnacle-red">Join &rarr;</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="text-base font-semibold text-text-primary mb-4">Past Meetings</h2>
          {pastMeetings.length === 0 ? (
            <div className="bg-white rounded-xl shadow-card p-12 text-center">
              <p className="text-text-muted">No meetings match the current filters.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone/20">
                    <th className={`${thClass} pl-6 pr-4 w-[140px]`}>Date</th>
                    <th className={thClass}>Meeting</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>Facilitator</th>
                    <th className={`${thClass} text-center`}>Duration</th>
                    <th className={`${thClass} text-center`}>Actions</th>
                    <th className={`${thClass} text-center`}>Rating</th>
                    <th className="w-10 px-2 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone/10">
                  {pastMeetings.map(meeting => (
                    <tr key={meeting.id} className="hover:bg-[#F5F5F5]/20 transition-colors group">
                      <td className="pl-6 pr-4 py-4">
                        <div className="text-sm font-medium text-text-primary">
                          {meeting.scheduled_date || '\u2014'}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm font-semibold text-text-primary">
                          {meeting.agenda_name}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-block px-2 py-0.5 rounded text-[12px] font-medium uppercase tracking-wide ${
                          meeting.status === 'completed' ? 'bg-status-complete/15 text-status-complete' :
                          meeting.status === 'cancelled' ? 'bg-stone/20 text-text-muted' :
                          'bg-charcoal/15 text-text-primary'
                        }`}>
                          {statusLabel(meeting.status)}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-text-secondary">
                        {meeting.facilitator_name || '\u2014'}
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="text-sm font-medium text-text-primary">
                          {meeting.duration_minutes ? formatDuration(meeting.duration_minutes) : '\u2014'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="text-sm text-text-secondary">
                          {meeting.action_items_count > 0 ? `${meeting.action_items_count} item${meeting.action_items_count !== 1 ? 's' : ''}` : '\u2014'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        {meeting.rating ? (
                          <span className="text-sm font-semibold text-text-primary">{Number(meeting.rating).toFixed(1)}</span>
                        ) : (
                          <span className="text-sm text-text-muted">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-2 py-4 text-right">
                        <Link
                          href={`/meetings/${meeting.id}`}
                          className="text-sm font-medium text-text-primary hover:underline pr-4"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <MeetingPagination
          basePath={basePath}
          filters={filters}
          page={page}
          totalCount={total_count}
          perPage={per_page}
        />
      </div>
    </Layout>
  )
}
